import { useState } from 'react';
import {
  useCreateSignupCode,
  useDeleteSignupCode,
  useSignupCodes,
  useUpdateSignupCode,
} from '../api/hooks.js';
import type { ApiSignupCode } from '../api/types.js';
import { ApiError } from '../api/client.js';
import { formatDate } from '../lib/format.js';
import { DASHBOARD_BASE_URL } from '../lib/origin.js';

export default function SignupCodesCard() {
  const list = useSignupCodes();
  const create = useCreateSignupCode();
  const update = useUpdateSignupCode();
  const remove = useDeleteSignupCode();

  const [code, setCode] = useState('');
  const [maxUses, setMaxUses] = useState('20');
  const [expiresAt, setExpiresAt] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<ApiSignupCode | null>(null);

  return (
    <section className="card p-5">
      <h2 className="text-sm font-semibold text-slate-700">Invite codes</h2>
      <p className="mt-1 text-sm text-slate-600">
        Shareable codes (e.g. for a meetup). Anyone with the code can sign up,
        up to the usage cap.
      </p>

      <form
        className="mt-4 grid gap-3 sm:grid-cols-2"
        onSubmit={async (e) => {
          e.preventDefault();
          setError(null);
          setCreated(null);
          try {
            const res = await create.mutateAsync({
              code: code.trim() || undefined,
              maxUses: maxUses ? Number(maxUses) : undefined,
              expiresAt: expiresAt ? new Date(expiresAt).toISOString() : undefined,
              note: note.trim() || undefined,
            });
            setCreated(res.code);
            setCode('');
            setNote('');
          } catch (err) {
            if (err instanceof ApiError) {
              const c = (err.payload as { error?: string })?.error ?? 'request_failed';
              setError(prettyError(c));
            } else {
              setError('Something went wrong.');
            }
          }
        }}
      >
        <div className="sm:col-span-2">
          <label className="label">Code (optional)</label>
          <input
            type="text"
            maxLength={32}
            placeholder="MEETUP24 (leave blank to auto-generate)"
            className="input mt-1 font-mono uppercase tracking-wider"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
          />
        </div>
        <div>
          <label className="label">Max uses</label>
          <input
            type="number"
            min={1}
            className="input mt-1"
            value={maxUses}
            onChange={(e) => setMaxUses(e.target.value)}
          />
        </div>
        <div>
          <label className="label">Expires (optional)</label>
          <input
            type="datetime-local"
            className="input mt-1"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
          />
        </div>
        <div className="sm:col-span-2">
          <label className="label">Note (internal)</label>
          <input
            type="text"
            maxLength={120}
            placeholder="e.g. Berlin meetup 2026"
            className="input mt-1"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
        <div className="sm:col-span-2 flex justify-end">
          <button type="submit" disabled={create.isPending} className="btn-primary">
            {create.isPending ? 'Creating…' : 'Create code'}
          </button>
        </div>
      </form>

      {error && (
        <p className="mt-2 rounded-md bg-red-50 p-2 text-sm text-red-700">{error}</p>
      )}
      {created && (
        <div className="mt-3 rounded-md bg-emerald-50 p-3 text-sm">
          <p className="font-medium text-emerald-800">Code ready to share</p>
          <p className="mt-1 font-mono text-emerald-700">{created.code}</p>
          <p className="mt-2 text-xs text-emerald-700">
            Share link:{' '}
            <code className="rounded bg-emerald-100 px-1 py-0.5">
              {DASHBOARD_BASE_URL}/signup?code={created.code}
            </code>
          </p>
        </div>
      )}

      <ul className="mt-5 divide-y divide-slate-100">
        {(list.data ?? []).length === 0 ? (
          <li className="py-3 text-sm text-slate-500">No invite codes yet.</li>
        ) : (
          (list.data ?? []).map((c) => (
            <li key={c.id} className="py-3">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                <div className="min-w-0">
                  <p className="truncate font-mono text-sm text-slate-900">{c.code}</p>
                  {c.note && <p className="truncate text-xs text-slate-500">{c.note}</p>}
                  <p className="text-xs text-slate-500">
                    {usageLabel(c)} · {statusLabel(c)} · created {formatDate(c.created_at)}
                  </p>
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1">
                  <button
                    className="text-xs text-slate-600 hover:underline"
                    onClick={() => {
                      const url = `${DASHBOARD_BASE_URL}/signup?code=${encodeURIComponent(c.code)}`;
                      navigator.clipboard.writeText(url);
                    }}
                  >
                    Copy link
                  </button>
                  <button
                    className="text-xs text-slate-600 hover:underline"
                    onClick={() => update.mutate({ id: c.id, patch: { is_active: !c.is_active } })}
                  >
                    {c.is_active ? 'Disable' : 'Enable'}
                  </button>
                  <button
                    className="text-xs text-red-600 hover:underline"
                    onClick={() => {
                      if (confirm(`Delete code ${c.code}? Past redemptions stay intact.`)) {
                        remove.mutate(c.id);
                      }
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </li>
          ))
        )}
      </ul>
    </section>
  );
}

function usageLabel(c: ApiSignupCode): string {
  if (c.max_uses == null) return `used ${c.used_count}× (unlimited)`;
  return `used ${c.used_count} / ${c.max_uses}`;
}

function statusLabel(c: ApiSignupCode): string {
  if (!c.is_active) return 'disabled';
  if (c.expires_at != null && c.expires_at <= Date.now()) return 'expired';
  if (c.max_uses != null && c.used_count >= c.max_uses) return 'exhausted';
  return 'active';
}

function prettyError(code: string): string {
  switch (code) {
    case 'code_taken': return 'That code is already in use.';
    case 'invalid_code_format': return 'Only letters, digits, and hyphens; 4–32 chars.';
    case 'invalid_expiration': return 'Expiration must be in the future.';
    default: return code.replace(/_/g, ' ');
  }
}
